import { InsuredPersonBasicDto } from '@zorro/clients';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import { Grid, Group, Icon, Text } from '@zorro/web/design-system';
import { useTextOverflow } from '@zorro/web/ui-utils';
import { ElementType } from 'react';

import { EnrollmentReviewCardCurrency } from './EnrollmentReviewCardCurrency';

type Props = {
  planName: string;
  carrierName: string;
  premium: number;
  insuredPeople: InsuredPersonBasicDto[];
  planNameInsuredColumnSpan: 3 | 8;
  withPlanNameAndInsuredColumns: boolean;
  icon: ElementType;
  isHighlighted?: boolean;
};

export const EnrollmentReviewCardPlanDetails = ({
  planName,
  carrierName,
  premium,
  insuredPeople,
  planNameInsuredColumnSpan,
  withPlanNameAndInsuredColumns,
  icon,
  isHighlighted,
}: Props) => {
  const insuredNames = insuredPeople
    .map(({ firstName }) => firstName)
    .join(', ');

  const carrierOverflow = useTextOverflow(carrierName);
  const planNameOverflow = useTextOverflow(planName);
  const insuredOverflow = useTextOverflow(insuredNames);
  const highlightedColor = isHighlighted
    ? 'var(--brand-purple-500)'
    : undefined;

  return (
    <>
      <Grid.Col span={planNameInsuredColumnSpan} pr="sm">
        <Tooltip
          label={carrierName}
          disabled={!carrierOverflow.isTextOverflowing}
        >
          <Group gap="xs" wrap="nowrap" style={{ overflow: 'hidden' }}>
            <Icon icon={icon} size={16} style={{ flexShrink: 0 }} />
            <Text
              ref={carrierOverflow.textRef}
              c={highlightedColor}
              size="sm"
              truncate
            >
              {carrierName}
            </Text>
          </Group>
        </Tooltip>
      </Grid.Col>
      {withPlanNameAndInsuredColumns && (
        <>
          <Grid.Col span={4} pr="xs" style={{ overflow: 'hidden' }}>
            <Tooltip
              label={planName}
              disabled={!planNameOverflow.isTextOverflowing}
            >
              <Text
                c="var(--color-text-default-secondary)"
                ref={planNameOverflow.textRef}
                size="sm"
                w="100%"
                truncate
              >
                {planName}
              </Text>
            </Tooltip>
          </Grid.Col>
          <Grid.Col span={3} pr="xs" style={{ overflow: 'hidden' }}>
            <Tooltip
              label={insuredNames}
              disabled={!insuredOverflow.isTextOverflowing}
            >
              <Text
                c="var(--color-text-default-secondary)"
                ref={insuredOverflow.textRef}
                size="sm"
                w="100%"
                truncate
              >
                ({insuredNames})
              </Text>
            </Tooltip>
          </Grid.Col>
        </>
      )}
      <Grid.Col span="auto">
        <EnrollmentReviewCardCurrency c={highlightedColor}>
          {premium}
        </EnrollmentReviewCardCurrency>
      </Grid.Col>
    </>
  );
};
